import { BlobWriter, ZipWriter } from "@zip.js/zip.js";
import { post } from "public/modules/post.js";
function supportsSavePicker() {
    return "showSaveFilePicker" in window;
}
/**
 * Has to be called before any `await`, as the browser allows opening
 * the picker only right after the user action.
 */
async function pickSaveTarget(suggestedName, type) {
    if (!supportsSavePicker())
        return { writable: undefined };
    try {
        const handle = await window.showSaveFilePicker({
            suggestedName,
            types: [
                {
                    description: type.description,
                    accept: { [type.mime]: [type.extension] },
                },
            ],
        });
        return { writable: await handle.createWritable() };
    }
    catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
            return undefined;
        }
        throw err;
    }
}
function saveBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}
async function fetchFile(url) {
    const response = await fetch(url);
    if (!response.ok || !response.body) {
        throw new Error(`Failed to download file, status ${response.status}`);
    }
    return response;
}
/**
 * Downloads the full size files of the given group images.
 * A single file is saved as it is, more files are packed into one zip.
 * @param zipName name of the zip file, without the extension
 */
export async function downloadFullSizeFiles(props) {
    var _a, _b, _c, _d, _e, _f;
    const imageNames = [...new Set(props.imageNames)];
    const isSingleFile = imageNames.length === 1;
    const target = await pickSaveTarget(isSingleFile ? imageNames[0] : `${props.zipName}.zip`, isSingleFile ?
        { description: "Zdjęcie", mime: "image/jpeg", extension: ".jpg" }
        : { description: "Plik zip", mime: "application/zip", extension: ".zip" });
    if (!target)
        return { status: "cancelled" };
    try {
        const response = await post(`/api/public/merchants/group/${props.groupId}/full-size/urls`, { imageNames });
        if (response.status !== 200) {
            await ((_a = target.writable) === null || _a === void 0 ? void 0 : _a.abort());
            return {
                status: response.reason === "no_full_size_files" ?
                    "no_full_size_files"
                    : "failed",
            };
        }
        const { files } = response;
        const total = files.length;
        (_b = props.onProgress) === null || _b === void 0 ? void 0 : _b.call(props, { done: 0, total });
        if (isSingleFile) {
            const fileResponse = await fetchFile(files[0].url);
            if (target.writable) {
                await fileResponse.body.pipeTo(target.writable);
            }
            else {
                saveBlob(await fileResponse.blob(), files[0].imageName);
            }
            (_c = props.onProgress) === null || _c === void 0 ? void 0 : _c.call(props, { done: 1, total });
            return { status: "downloaded" };
        }
        const blobWriter = target.writable ? undefined : new BlobWriter("application/zip");
        // Photos are already compressed, so they are only stored
        const zipWriter = new ZipWriter((_d = target.writable) !== null && _d !== void 0 ? _d : blobWriter, {
            level: 0,
        });
        // One by one, so only a single file is kept in memory at once
        for (const [index, file] of files.entries()) {
            const fileResponse = await fetchFile(file.url);
            await zipWriter.add(file.imageName, fileResponse.body);
            (_e = props.onProgress) === null || _e === void 0 ? void 0 : _e.call(props, { done: index + 1, total });
        }
        await zipWriter.close();
        if (blobWriter) {
            saveBlob(await blobWriter.getData(), `${props.zipName}.zip`);
        }
        return { status: "downloaded" };
    }
    catch (err) {
        console.error(err);
        await ((_f = target.writable) === null || _f === void 0 ? void 0 : _f.abort().catch(() => { }));
        return { status: "failed" };
    }
}
